import { Box, Card, IconButton, Tooltip, Typography, Grid, CardMedia, CardContent, CardActions, Chip } from '@mui/material';
import React, { useEffect, useState, useCallback } from 'react';
import { ContentCopy, Delete, OpenInNew } from '@mui/icons-material';
import Swal from 'sweetalert2';
import Loading from '../../components/Loader/Loading';
import NoRowsLayout from '../../components/Layout/NoRowsLayout';
import { MediaServices } from './MediaServices';

const ImageList = ({ refreshTrigger, currentTab }) => {
    const [imageList, setImageList] = useState([]);
    const [loading, setLoading] = useState(false);
    const [totalRowCount, setTotalRowCount] = useState(0);

    const fetchImageList = useCallback(async () => {
        setLoading(true);
        try {
            const path = currentTab === 'whatsapp' ? 'whatsapp/image' : 'image';
            const response = await MediaServices.fetchImageList(path);
            if (response.success === '1' || response.success === 1) {
                const imagesWithId = (response.data || []).map((image, index) => ({
                    ...image,
                    id: image.id || index + 1,
                    createdAt: image.lastModified
                }));
                setImageList(imagesWithId);
                setTotalRowCount(response.total || response.data?.length || 0);
            } else {
                console.error('Failed to fetch images:', response.message);
            }
        } catch (error) {
            console.error('Error fetching images:', error);
        } finally {
            setLoading(false);
        }
    }, [currentTab]);

    useEffect(() => {
        fetchImageList();
    }, [refreshTrigger, fetchImageList]);

    const handleCopyLink = (url) => {
        navigator.clipboard.writeText(url).then(() => {
            Swal.fire({
                icon: 'success',
                title: 'Copied!',
                text: 'Image URL copied to clipboard',
                timer: 1500,
                showConfirmButton: false,
            });
        }).catch(() => {
            Swal.fire({
                icon: 'error',
                title: 'Failed',
                text: 'Could not copy URL to clipboard',
            });
        });
    };

    const handleOpenLink = (url) => {
        window.open(url, '_blank');
    };

    const handleDelete = async (id) => {
        const result = await Swal.fire({
            title: 'Are you sure?',
            text: 'This image will be permanently deleted!',
            icon: 'warning',
            showCancelButton: true,
            confirmButtonText: 'Yes, delete it!',
            cancelButtonText: 'Cancel',
        });

        if (result.isConfirmed) {
            try {
                const response = await MediaServices.deleteImage(id);
                if (response.success === '1' || response.success === 1) {
                    Swal.fire({
                        icon: 'success',
                        title: 'Deleted!',
                        text: 'Image has been deleted',
                        timer: 1500,
                    });
                    fetchImageList();
                } else {
                    Swal.fire({
                        icon: 'error',
                        title: 'Failed',
                        text: response.message || 'Failed to delete image',
                    });
                }
            } catch (error) {
                Swal.fire({
                    icon: 'error',
                    title: 'Error',
                    text: 'Something went wrong while deleting',
                });
            }
        }
    };

    return (
        <Box sx={{ height: '100%' }}>
            <Box sx={{ 
                p: 2, 
                borderBottom: '1px solid #e0e0e0', 
                bgcolor: '#f5f5f5', 
                display: 'flex', 
                justifyContent: 'space-between', 
                alignItems: 'center',
                borderRadius: '8px 8px 0 0'
            }}>
                <Typography sx={{ fontWeight: 600, fontSize: { xs: '0.85rem', md: '0.9rem' } }}>
                    Image Gallery
                </Typography>
                <Chip 
                    label={`${totalRowCount} images`} 
                    size="small" 
                    sx={{ fontSize: '0.75rem' }}
                />
            </Box>

            {loading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 400 }}>
                    <Loading />
                </Box>
            ) : imageList.length === 0 ? (
                <Box sx={{ minHeight: 400 }}>
                    <NoRowsLayout />
                </Box>
            ) : (
                <Box sx={{ p: { xs: 1, sm: 2, md: 3 } }}>
                    <Grid container spacing={{ xs: 2, sm: 2.5, md: 3 }}>
                        {imageList.map((image) => (
                            <Grid item xs={12} sm={6} md={4} lg={3} key={image.id}>
                                <Card 
                                    sx={{ 
                                        height: '100%',
                                        display: 'flex',
                                        flexDirection: 'column',
                                        transition: 'all 0.3s',
                                        '&:hover': {
                                            transform: 'translateY(-4px)',
                                            boxShadow: 4
                                        }
                                    }}
                                >
                                    <CardMedia
                                        component="img"
                                        src={image.url}
                                        alt={image.fileName}
                                        sx={{ 
                                            height: { xs: 180, sm: 200, md: 220 },
                                            objectFit: 'cover',
                                            cursor: 'pointer'
                                        }}
                                        onClick={() => handleOpenLink(image.url)}
                                    />
                                    <CardContent sx={{ flexGrow: 1, p: 2 }}>
                                        <Tooltip title={image.fileName}>
                                            <Typography 
                                                variant="body2" 
                                                sx={{ 
                                                    fontWeight: 500,
                                                    mb: 1.5,
                                                    overflow: 'hidden',
                                                    textOverflow: 'ellipsis',
                                                    whiteSpace: 'nowrap',
                                                    fontSize: '0.875rem'
                                                }}
                                            >
                                                {image.fileName}
                                            </Typography>
                                        </Tooltip>
                                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                                <Typography variant="caption" color="text.secondary">
                                                    Size:
                                                </Typography>
                                                <Typography variant="caption" sx={{ fontWeight: 500 }}>
                                                    {image.size ? (image.size / (1024 * 1024)).toFixed(2) + ' MB' : '-'}
                                                </Typography>
                                            </Box>
                                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                                <Typography variant="caption" color="text.secondary">
                                                    Uploaded:
                                                </Typography>
                                                <Typography variant="caption" sx={{ fontWeight: 500 }}>
                                                    {image.createdAt ? new Date(image.createdAt).toLocaleDateString() : '-'}
                                                </Typography>
                                            </Box>
                                        </Box>
                                    </CardContent>
                                    <CardActions sx={{ p: 2, pt: 0, justifyContent: 'space-between' }}>
                                        <Box sx={{ display: 'flex', gap: 0.5 }}>
                                            <Tooltip title="Copy URL">
                                                <IconButton
                                                    size="small"
                                                    color="primary"
                                                    onClick={() => handleCopyLink(image.url)}
                                                >
                                                    <ContentCopy fontSize="small" />
                                                </IconButton>
                                            </Tooltip>
                                            <Tooltip title="Open in New Tab">
                                                <IconButton
                                                    size="small"
                                                    color="info"
                                                    onClick={() => handleOpenLink(image.url)}
                                                >
                                                    <OpenInNew fontSize="small" />
                                                </IconButton>
                                            </Tooltip>
                                        </Box>
                                        <Tooltip title="Delete">
                                            <IconButton
                                                size="small"
                                                color="error"
                                                onClick={() => handleDelete(image.id)}
                                            >
                                                <Delete fontSize="small" />
                                            </IconButton>
                                        </Tooltip>
                                    </CardActions>
                                </Card>
                            </Grid>
                        ))}
                    </Grid>
                </Box>
            )}
        </Box>
    );
};

export default ImageList;
